import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CreditCard, Loader2, Plus, Save, Truck } from 'lucide-react';
import { Switch } from '../../ui/switch';
import { CampoTexto, Panel } from '../../bodas/admin/comunes';
import BotonesOrden from './BotonesOrden';
import { adminApi, claveDe, errorApi, mover, nuevoEnvio } from './tiendaAdminApi';

const EditorEnvio = ({ envio, indice, total, onCambiar, onMover, onEliminar }) => {
  const set = (campo, valor) => onCambiar({ ...envio, [campo]: valor });
  const t = `tienda-envio-${indice}`;
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={t}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700">Opción {indice + 1}</span>
        <BotonesOrden indice={indice} total={total} onMover={onMover} onEliminar={onEliminar} etiqueta="opción de envío" testId={t} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <CampoTexto label="Título" value={envio.titulo} onChange={v => set('titulo', v)} maxLength={60} testId={`${t}-titulo`} />
        <CampoTexto label="Costo que verá el cliente" ayuda="Ej. Sin costo · Se paga al recibir" value={envio.costo} onChange={v => set('costo', v)} maxLength={120} testId={`${t}-costo`} />
      </div>
      <CampoTexto label="Descripción" value={envio.descripcion} onChange={v => set('descripcion', v)} maxLength={300} testId={`${t}-descripcion`} />
      <div className="flex flex-wrap gap-5 text-sm text-slate-700">
        <label className="flex items-center gap-2"><Switch checked={envio.pide_direccion} onCheckedChange={v => set('pide_direccion', v)} className="min-h-0 data-[state=checked]:bg-emerald-600" data-testid={`${t}-direccion`} />Pedir dirección</label>
        <label className="flex items-center gap-2"><Switch checked={envio.activo} onCheckedChange={v => set('activo', v)} className="min-h-0 data-[state=checked]:bg-emerald-600" data-testid={`${t}-activo`} />Activa</label>
      </div>
    </div>
  );
};

export default function AjustesTienda() {
  const [ajustes, setAjustes] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    adminApi.ajustes().then(r => setAjustes(r.data)).catch(e => toast.error(errorApi(e, 'No se pudieron cargar los ajustes')));
  }, []);

  if (!ajustes) return <div className="h-64 animate-pulse rounded-xl bg-slate-200" />;

  const set = (campo, valor) => setAjustes(a => ({ ...a, [campo]: valor }));
  const envios = ajustes.envio_opciones;

  const guardar = async () => {
    if (envios.some(e => !e.titulo.trim())) { toast.error('Todas las opciones de envío necesitan un título'); return; }
    setGuardando(true);
    try {
      const r = await adminApi.guardarAjustes(ajustes);
      setAjustes(r.data);
      toast.success('Ajustes guardados');
    } catch (e) {
      toast.error(errorApi(e, 'No se pudieron guardar los ajustes'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="tienda-ajustes">
      <Panel className="space-y-3 p-5">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800"><CreditCard className="h-5 w-5 text-amber-600" />Pago</h2>
        <CampoTexto label="Enlace de pago de Bold" value={ajustes.enlace_pago} onChange={v => set('enlace_pago', v)} testId="tienda-enlace-pago"
          ayuda="En este enlace el cliente escribe el valor; Mi JPM le muestra el valor exacto a pagar. Gerencia verifica cada pago reportado." />
        <CampoTexto label="Instrucciones que verá el cliente antes de pagar" multiline rows={6} value={ajustes.instrucciones_pago}
          onChange={v => set('instrucciones_pago', v)} maxLength={1500} testId="tienda-instrucciones-pago" />
      </Panel>
      <Panel className="space-y-3 p-5">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800"><Truck className="h-5 w-5 text-amber-600" />Opciones de envío</h2>
        <p className="text-sm text-slate-500">El envío no se cobra en el pago online: aquí informas las opciones y su costo.</p>
        {envios.map((e, i) => (
          <EditorEnvio key={claveDe(e)} envio={e} indice={i} total={envios.length}
            onCambiar={v => set('envio_opciones', envios.map((x, j) => (j === i ? v : x)))}
            onMover={d => set('envio_opciones', mover(envios, i, d))}
            onEliminar={envios.length > 1 ? () => set('envio_opciones', envios.filter((_, j) => j !== i)) : undefined} />
        ))}
        <button type="button" onClick={() => set('envio_opciones', [...envios, nuevoEnvio()])} className="inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-400 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="tienda-agregar-envio">
          <Plus className="h-4 w-4" />Agregar opción de envío
        </button>
      </Panel>
      <div className="flex justify-end">
        <button onClick={guardar} disabled={guardando} className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-800 disabled:opacity-60" data-testid="tienda-guardar-ajustes">
          {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Guardar ajustes
        </button>
      </div>
    </div>
  );
}
